<template>
  <article class="PlanDisplayCard u-card-bordered" :class="{ [`PlanDisplayCard--${color}`]: !!color }">
    <img
      v-if="imageUrl"
      class="PlanDisplayCard-image"
      :src="imageUrl"
      :alt="title"
      loading="lazy"
    >

    <slot name="tag">
      <Tag
        v-if="tag"
        type="highlight"
        variant="filled"
        class="PlanDisplayCard-tag"
      >
        {{ tag }}
      </Tag>
    </slot>

    <div class="PlanDisplayCard-container">
      <div class="PlanDisplayCard-titleContainer">
        <slot name="highlight">
          <Tag
            v-if="highlight"
            type="neutral"
            variant="outlined"
            size="xs"
            class="PlanDisplayCard-highlight"
          >
            {{ highlight }}
          </Tag>
        </slot>

        <slot name="title">
          <h4 v-if="title" class="PlanDisplayCard-title">
            {{ title }}
          </h4>
        </slot>

        <slot name="subtitle">
          <p v-if="subtitle" class="PlanDisplayCard-subtitle">
            {{ subtitle }}
          </p>
        </slot>
      </div>

      <slot name="tripTypeSelector">
        <RadioButtons
          v-if="tripTypeOptions"
          v-model="tripType"
          class="PlanDisplayCard-tripTypeSelector"
          :options="tripTypeOptions"
        />
      </slot>

      <slot name="benefits">
        <PlanBenefits
          v-if="benefits"
          class="PlanDisplayCard-benefits"
          :benefits="benefits"
        />
      </slot>

      <ul v-if="commitments?.length" class="PlanDisplayCard-commitments">
        <li v-for="commitment of commitments" :key="commitment.text" class="PlanDisplayCard-commitment">
          <Icon :icon="commitment.icon" size="sm" />
          <span>{{ commitment.text }}</span>
        </li>
      </ul>

      <div class="PlanDisplayCard-priceContainer">
        <span v-if="originalPriceLabel" class="PlanDisplayCard-originalPriceLabel">{{ originalPriceLabel }}</span>

        <div class="PlanDisplayCard-offerRow">
          <s v-if="originalPrice" class="PlanDisplayCard-originalPrice">{{ originalPriceLine }}</s>
          <!-- Rendered even without an offer (hidden) so the price block has the
               same height on every card and the commitments boxes stay aligned. -->
          <Tag
            :type="offerTag?.type ?? 'warning'"
            variant="filled"
            size="xs"
            :leftIcon="offerTag?.icon"
            class="PlanDisplayCard-offerTag"
            :class="{ 'is-hidden': !offerTag }"
          >
            {{ offerTag?.label }}
          </Tag>
        </div>

        <div class="PlanDisplayCard-priceGroup">
          <span v-if="priceLabel" class="PlanDisplayCard-priceLabel">{{ priceLabel }}</span>
          <div class="PlanDisplayCard-priceValue">
            <slot name="price">
              <span v-if="price" class="PlanDisplayCard-price">{{ price }}</span>
            </slot>
            <span v-if="frequency" class="PlanDisplayCard-priceRecurrency">
              /{{ frequency }}
            </span>
            <Tooltip v-if="priceInfo">
              <template #trigger>
                <Icon icon="information-outline" size="sm" class="PlanDisplayCard-priceInfoIcon" />
              </template>
              {{ priceInfo }}
            </Tooltip>
          </div>
        </div>
      </div>

      <Callout v-if="priceNote" size="sm" class="PlanDisplayCard-priceNote">
        {{ priceNote }}
      </Callout>

      <ActionButton
        v-if="submitLabel"
        size="md"
        class="PlanDisplayCard-submitButton"
        @click="submit"
      >
        {{ submitLabel }}
      </ActionButton>

      <!-- Mobile shows the price disclaimer as static text below the CTA instead of the ⓘ hover tooltip. -->
      <p v-if="priceInfo" class="PlanDisplayCard-priceDisclaimer">
        <Icon icon="information-outline" size="sm" />
        {{ priceInfo }}
      </p>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Icon from '@cvo/ui/components/atoms/Icon/Icon.vue';
import Tag from '@cvo/ui/components/atoms/Tag/Tag.vue';
import Callout from '@cvo/ui/components/atoms/Callout/Callout.vue';
import Tooltip from '@cvo/ui/components/atoms/Tooltip/Tooltip.vue';
import ActionButton from '@cvo/ui/components/atoms/ActionButton/ActionButton.vue';
import PlanBenefits from '@cvo/ui/components/molecules/PlanBenefits/PlanBenefits.vue';
import type { PlanBenefit } from '@cvo/ui/components/molecules/PlanBenefits/PlanBenefits.model';
import RadioButtons from '@cvo/ui/components/atoms/RadioButtons/RadioButtons.vue';
import type { RadioOption } from '@cvo/ui/components/atoms/RadioButtons/RadioButtons.model';
import type { OfferTag } from './BasePlanDisplayCard.model';

interface Props {
  planId: string;
  imageUrl?: string;
  color?: string;
  tag?: string;
  title?: string;
  subtitle?: string;
  highlight?: string;
  tripTypeOptions?: RadioOption[];
  price?: string;
  originalPrice?: string;
  originalPriceLabel?: string;
  offerTag?: OfferTag;
  priceNote?: string;
  priceInfo?: string;
  priceLabel?: string;
  frequency?: string;
  benefits?: PlanBenefit[];
  commitments?: PlanBenefit[];
  submitLabel?: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'submit', planId: string): void;
}>();

const tripType = defineModel<string | null>('type', {
  default: (properties: Props) => (
    properties.tripTypeOptions && properties.tripTypeOptions.length > 0
      ? (properties.tripTypeOptions[0].value)
      : null
    ),
});

// The struck-through full price mirrors the regular price line ("€159 /mo").
const originalPriceLine = computed(() => [
  props.originalPrice,
  props.frequency && `/${props.frequency}`,
].filter(Boolean).join(' '));

const selectedPlanId = computed(() => {
  if (!props.tripTypeOptions || !tripType.value) {
    return props.planId;
  }
  return tripType.value;
});

function submit() {
  emit('submit', selectedPlanId.value);
}
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/text';
@use '@cvo/ui/styles/utils/responsive';

@layer cvo-ui {
  .PlanDisplayCard {
    position: relative;
    display: flex;
    flex-direction: column;
    background-color: vars.$organisms-card-plandisplay-bg-color;
    border: vars.$organisms-card-plandisplay-border-stroke solid vars.$organisms-card-plandisplay-border-color;
    border-radius: vars.$organisms-card-newplandisplay-lg-md-border-radius;
    overflow: hidden;

    @include responsive.mobile {
      border-radius: vars.$organisms-card-newplandisplay-sm-border-radius;
    }

    @include responsive.tablet {
      width: 360px;
    }

    &-image {
      width: 100%;
      height: 120px;
      object-fit: cover;
    }

    &-tag {
      position: absolute;
      top: vars.$dimensions-d-4;
      inset-inline-end: vars.$dimensions-d-4;
    }

    &-container {
      display: flex;
      flex-direction: column;
      // Uniform section rhythm from the design (newplandisplay spacing), replacing per-section margins.
      gap: vars.$organisms-card-newplandisplay-lg-md-spacing;

      padding: vars.$organisms-card-newplandisplay-lg-md-padding;
      flex: 1;

      @include responsive.mobile {
        gap: vars.$organisms-card-newplandisplay-sm-spacing;
        padding: vars.$organisms-card-newplandisplay-sm-padding;
      }
    }

    &-titleContainer {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-1;
    }

    &-benefits {
      flex-grow: 1;
      --plan-benefits-icon-color: #{vars.$atoms-benefit-icon-color};
    }

    &-highlight {
      // Rendered as an outlined Tag pill; align-self keeps it hugging its label
      // instead of stretching across the title column. Colours/border come from Tag.
      align-self: flex-start;
    }

    &-title {
      color: vars.$organisms-card-plandisplay-planname-color;
    }

    &-subtitle {
      color: vars.$organisms-card-plandisplay-secondary-text-color;
      @include text.heading(h6);
    }

    &-tripTypeSelector {
      flex: 0;
      background-color: vars.$organisms-card-plandisplay-segmenttype-bg-color;
      border: vars.$organisms-card-plandisplay-border-stroke solid vars.$organisms-card-plandisplay-border-color;
      border-radius: vars.$organisms-card-plandisplay-segmenttype-border-radius;
      padding: vars.$dimensions-d-3;

      &:focus-within {
        border-color: vars.$color-brand-secondary-500;
      }
    }

    &-priceContainer {
      display: flex;
      flex-direction: column;
      // Anchor the price + button to the card bottom; the flexible space sits above the price.
      margin-block-start: auto;
    }

    &-offerRow {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: vars.$dimensions-d-2;
      margin-block-end: vars.$dimensions-d-1;
      flex-wrap: wrap-reverse;
    }

    &-offerTag {
      margin-inline-start: auto;

      &.is-hidden {
        visibility: hidden;
      }
    }

    &-originalPriceLabel {
      margin-block-end: vars.$dimensions-d-3;
      color: vars.$organisms-card-plandisplay-secondary-text-color;
      @include text.text(sm);
    }

    &-priceNote {
      &.Callout {
        // Landing sections tint callouts with their own surface; the card keeps the callout token.
        --section-surface-color: initial;
      }
    }

    &-originalPrice {
      color: vars.$organisms-card-newplandisplay-strikethrough-text-color;
      @include text.text(sm);
    }

    &-priceInfoIcon {
      align-self: baseline;
      color: vars.$organisms-card-newplandisplay-icon-color;

      // Mobile surfaces the disclaimer below the CTA instead, so drop the hover-only ⓘ here.
      @include responsive.mobile {
        display: none;
      }
    }

    &-priceDisclaimer {
      display: flex;
      gap: vars.$dimensions-d-2;

      color: vars.$organisms-card-newplandisplay-disclaimer-text-color;
      @include text.text(xs);

      .Icon {
        flex-shrink: 0;
        color: vars.$organisms-card-newplandisplay-icon-color;
      }

      @include responsive.tablet {
        display: none;
      }
    }

    &-commitments {
      display: flex;
      flex-direction: column;
      // 6px from the Figma design, composed from primitives (no d-1-5 exists).
      gap: vars.$dimensions-d-1 + vars.$dimensions-d-0-5;

      width: 100%;
      padding: vars.$dimensions-d-3;

      list-style: none;
      background-color: vars.$atoms-commitments-bg-color;
      // Plain band: full-bleed top/bottom rules only, no side borders or rounded corners.
      border-block: vars.$dimensions-d-0-25 solid vars.$atoms-commitments-border-color;
    }

    &-commitment {
      display: flex;
      gap: vars.$dimensions-d-2;
      align-items: flex-start;

      color: vars.$atoms-commitments-text-color;
      @include text.text(xs);
    }

    &-priceGroup {
      display: flex;
      flex-wrap: wrap;
      gap: vars.$dimensions-d-2;
      align-items: baseline;
      justify-content: flex-start;
    }

    &-priceLabel {
      color: vars.$organisms-card-plandisplay-secondary-text-color;
      @include text.text(sm);
    }

    &-price {
      color: vars.$organisms-card-plandisplay-price-color;
      @include text.heading(h2);
    }

    &-priceRecurrency {
      color: vars.$organisms-card-plandisplay-price-color;
      @include text.text(lg);
    }
  }
}
</style>
